import React, { useState } from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { reduxForm, InjectedFormProps } from 'redux-form';
import { ThunkDispatch } from 'redux-thunk';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';

import { hrisIntegrationResponseForOrgIdSelector } from '../../hris-integration-response/hris-integration-response-selectors';
import { mergeIntegrationForOrgIdSelector } from '../../merge-integration/merge-integration-selectors';
import { userSelector } from '../../reducers/current-user-selectors';
import { updateEmployee } from '../actions/update-employee';

import { EmployeeGroups } from '../../employee-groups/components/employee-groups';
import { UiButton } from '../../ui/ui-button';
import { EmployeeSupervisors } from '../employee-supervisors';

import { EmployeeProfileEdit } from './employee-profile-edit';

import { getHrisDisplayName } from '../../merge-integration/util/merge-display-utils';
import { EmployeeCanMessage, PersonCanMessage } from '../../util/can-message';
import * as Formatters from '../../util/formatters';
import { formatPhoneNumber } from '../../util/formatters';
import { LANGUAGES } from '../../util/languages';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { EMPLOYEE_PERMISSION } from '../../util/permissions';
import { confirm } from '../../widgets/confirmation-dialog';
import { LoadingMessage } from '../../widgets/loading-message';
import { employeeName } from '../utils/employee-name';

const mapStateToProps = (state: any, { employee }: InputProps) => {
  const initialValues = {
    ...employee,
    EmployeeGroups: undefined,
    Supervisors: undefined,
  };
  initialValues.Contact.Person.phoneNumber = formatPhoneNumber(
    initialValues.Contact.Person.phoneNumber,
  );

  return {
    user: userSelector(state),
    initialValues,
    mergeIntegration: mergeIntegrationForOrgIdSelector(state, employee.orgId),
    hrisIntegrationResponse: hrisIntegrationResponseForOrgIdSelector(state, employee.orgId),
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { employee }: InputProps,
) => ({
  actions: {
    updateEmployee: (update: Partial<EmployeeUpdateRequest>) =>
      dispatch(updateEmployee(employee.employeeId, update)),
  },
});

interface InputProps {
  employee: Employee;
}

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

// redux-form has a very complicated type. Basically, the first parameter of the generic InjectedFormProps
// describes the values in the form, and the second parameter is the props that will be passed through to
// your component. You have to specify both and then in the call below, also specify both.
type EmployeeDetailsComponentProps = InputProps & ReduxProps & DispatchProps;
type Props = InjectedFormProps<Partial<EmployeeUpdateRequest>, EmployeeDetailsComponentProps> &
  EmployeeDetailsComponentProps;

const ReduxFormConfig = {
  enableReinitialize: true,
  form: 'employee-details',
};

const Section = (props: { title: string; children: React.ReactNode }) => (
  <Card className="u-margin-top">
    <CardContent>
      <div className="typo-h6">{props.title}</div>
      {props.children}
    </CardContent>
  </Card>
);

const ProfileItem = (props: {
  title: string;
  value: string | null;
  icon?: React.ReactNode | null;
}) => (
  <div className="u-margin-top-lg">
    <div className="typo-label-v2">{props.title}</div>
    <div className="u-flex-center">
      {props.icon}
      <div className="typo-title">{props.value || '--'}</div>
    </div>
  </div>
);

export const EmployeeDetails = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm<Partial<EmployeeUpdateRequest>, EmployeeDetailsComponentProps>(ReduxFormConfig)(
    ({
      employee,
      user,
      mergeIntegration,
      hrisIntegrationResponse,
      actions,
      handleSubmit,
      pristine,
      reset,
    }: Props) => {
      const [isEditing, setIsEditing] = useState<boolean>(false);

      const handleEditClick = () => {
        setIsEditing(true);
      };

      const handleCancelClick = () => {
        setIsEditing(false);
        reset();
      };

      const renderDuration = (duration: moment.Duration) => {
        const years = duration.years();
        const months = duration.months();
        const days = duration.days();
        if (duration.asMilliseconds() < 0) {
          return `Starts in ${Math.ceil(duration.asDays() * -1)} days`;
        }
        if (!years && !months && !days) {
          return 'Started Today';
        }

        return `${years ? `${years} year${years !== 1 ? 's' : ''}` : ''}${
          months ? ` ${months} month${months !== 1 ? 's' : ''}` : ''
        } ${days ? ` ${days} day${days !== 1 ? 's' : ''}` : ''}`;
      };

      const save = (values: Partial<EmployeeUpdateRequest>) => {
        actions.updateEmployee(values);
        setIsEditing(false);
      };

      const handleOptOutClick = async () => {
        if (
          await confirm(
            `Are you sure you want ${employeeName(employee)} to stop receiving text messages?`,
          )
        ) {
          actions.updateEmployee({ optedOutOfTexts: true, manuallyOptedOutDate: moment.utc() });
        }
      };

      const handleOptInClick = () => {
        actions.updateEmployee({ optedOutOfTexts: false, manuallyOptedOutDate: null });
      };

      const renderEmploymentDetails = () => {
        const hasAllEmployeesPermission = user.employeesPermission === EMPLOYEE_PERMISSION.ALL;

        return (
          <>
            {employee.jobTitle && <ProfileItem title="Job Title" value={employee.jobTitle} />}
            {employee.externalId && (
              <ProfileItem title="Employee Identifier" value={employee.externalId} />
            )}
            {hasAllEmployeesPermission && (
              <div className="u-margin-top-lg">
                <div className="typo-label-v2">Supervisor</div>
                <EmployeeSupervisors employee={employee} />
              </div>
            )}
            <ProfileItem
              title="Started"
              value={
                employee.startDate ? moment.utc(employee.startDate).format('MMMM D, YYYY') : null
              }
            />
            {employee.endDate && (
              <ProfileItem
                title="Ended"
                value={moment.utc(employee.endDate).format('MMMM D, YYYY')}
              />
            )}
            {/* Don't show tenure if the person's employment has ended, but we don't know when. */}
            {employee.startDate && (employee.isCurrentEmployee || employee.endDate) && (
              <ProfileItem
                title="Tenure"
                value={renderDuration(
                  moment.duration(
                    (employee.endDate ? moment(employee.endDate) : moment.utc()).diff(
                      moment.utc(employee.startDate),
                    ),
                  ),
                )}
              />
            )}
            {!employee.isCurrentEmployee && employee.EndEmploymentReason && (
              <ProfileItem
                title="Reason"
                value={`${employee.EndEmploymentReason.type} - ${
                  employee.EndEmploymentReason.requiresExplanation
                    ? employee.endReasonExplanation || 'No Reason Given'
                    : employee.EndEmploymentReason.endReasonDescription
                }
            `}
              />
            )}
          </>
        );
      };

      const renderDetails = () => {
        const {
          Contact: {
            Person: { email, phoneNumber, birthdate, languagePreference },
          },
        } = employee;
        const canMessage = new EmployeeCanMessage(employee);
        const { canText, errorMessage } = canMessage.canTextMessage();
        return (
          <>
            <ProfileItem title="Email" value={email} />
            <ProfileItem
              title="Cell Phone"
              value={Formatters.formatPhoneNumber(phoneNumber)}
              icon={
                !canText ? (
                  <MaterialDesignIcon iconName={ICON_NAMES.WARNING} color="secondary" />
                ) : null
              }
            />
            {!canText ? (
              <div className="typo-subtitle u-color-secondary">{errorMessage}</div>
            ) : null}
            {phoneNumber ? renderOptOutSection() : null}

            <ProfileItem
              title="Birthdate"
              value={birthdate ? moment(birthdate).format('MM/DD/YYYY') : null}
            />
            <ProfileItem
              title="Language Preference"
              value={LANGUAGES[languagePreference || ''] || 'English (default)'}
            />
          </>
        );
      };

      const renderOptOutSection = () => {
        // Use the person version of can text here to see if it's even possible to text the number, regardless
        // of if they have been opted out at the employee level
        const canMessage = new PersonCanMessage(employee.Contact.Person);
        const { canText } = canMessage.canTextMessage();

        const optedOut = employee.optedOutOfTexts;
        const manuallyOptedOut = Boolean(employee.manuallyOptedOutDate);

        const buttonText = optedOut ? 'OPT-IN TO TEXTS' : 'OPT-OUT OF TEXTS';

        const optInHandler = manuallyOptedOut ? handleOptInClick : undefined; // Only applies when manually opted out
        const buttonAction = optedOut ? optInHandler : handleOptOutClick;

        const cannotOptBackIn = optedOut && !manuallyOptedOut;
        const buttonExplanation = cannotOptBackIn
          ? 'This person opted out via text and can only be opted back in by them texting "START"'
          : null;

        return (
          <div className="u-display-flex u-alignItems-center u-margin-top-sm">
            <UiButton
              variant="tertiary"
              size="xxs"
              onClick={buttonAction}
              disabled={!canText || cannotOptBackIn}
            >
              {buttonText}
            </UiButton>
            {buttonExplanation ? (
              <span className="typo-subtitle u-margin-left-sm">{buttonExplanation}</span>
            ) : null}
          </div>
        );
      };

      if (!employee) {
        return <LoadingMessage />;
      }

      if (isEditing) {
        return (
          <form onSubmit={handleSubmit(save)}>
            <div className="u-display-flex u-justifyContent-flexEnd">
              <UiButton size="sm" variant="dismiss" onClick={handleCancelClick}>
                Cancel
              </UiButton>
              <UiButton
                size="sm"
                variant="primary"
                className="u-margin-left"
                type="submit"
                disabled={pristine}
              >
                Save
              </UiButton>
            </div>
            <EmployeeProfileEdit employee={employee} orgId={employee.orgId} />
          </form>
        );
      }

      const hasIntegrationOverrides = Boolean(
        employee.integrationId && Object.keys(employee.integrationOverrides ?? {}).length,
      );
      const hrisDisplayName = getHrisDisplayName(mergeIntegration, hrisIntegrationResponse);

      return (
        <>
          <div
            className={
              hasIntegrationOverrides ? 'u-spread' : 'u-display-flex u-justifyContent-flexEnd'
            }
          >
            {hasIntegrationOverrides && (
              <div className="typo-subtitle">
                Employee details have been edited and differ from{' '}
                {hrisDisplayName || 'your HR/payroll system'}
              </div>
            )}
            <UiButton size="sm" onClick={handleEditClick}>
              Edit
            </UiButton>
          </div>
          <Section title="Personal Information">{renderDetails()}</Section>
          <Section title="Employment Details">{renderEmploymentDetails()}</Section>
          <Section title="Groups">
            <EmployeeGroups employee={employee} />
          </Section>
        </>
      );
    },
  ),
);
